import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

export interface ChatToast {
  id: string;
  roomId: string;
  roomName: string;
  senderName: string;
  content: string;
  type: 'message' | 'mention';
  timestamp: Date;
}

/** Última mensagem que passou por uma sala nesta sessão, por roomId. */
export interface UltimaMensagemDaSala {
  conteudo: string;
  em: string;
}

interface ChatPopupContextType {
  isOpen: boolean;
  isMinimized: boolean;
  unreadCount: number;
  unreadByRoom: Record<string, number>;
  lastMessageByRoom: Record<string, UltimaMensagemDaSala>;
  hasNewMentions: boolean;
  toasts: ChatToast[];
  openChat: () => void;
  closeChat: () => void;
  minimizeChat: () => void;
  maximizeChat: () => void;
  toggleChat: () => void;
  incrementUnreadCount: (roomId: string) => void;
  hidratarNaoLidas: (porSala: Record<string, number>, ignorar?: string[]) => void;
  registerRoomMessage: (roomId: string, conteudo: string, em: string) => void;
  clearRoomUnread: (roomId: string) => void;
  setHasNewMentions: (mentions: boolean) => void;
  clearNotifications: () => void;
  pushToast: (toast: Omit<ChatToast, 'id' | 'timestamp'>) => void;
  dismissToast: (id: string) => void;
}

export const ChatPopupContext = createContext<ChatPopupContextType | undefined>(undefined);

export const useChatPopup = () => {
  const context = useContext(ChatPopupContext);
  if (!context) {
    throw new Error('useChatPopup must be used within a ChatPopupProvider');
  }
  return context;
};

interface ChatPopupProviderProps {
  children: React.ReactNode;
}

export function ChatPopupProvider({ children }: ChatPopupProviderProps) {
  // Recuperar estado salvo inicial do localStorage
  const getInitialState = () => {
    const savedState = localStorage.getItem('chatPopupState');
    if (savedState) {
      try {
        const { isOpen: savedIsOpen, isMinimized: savedIsMinimized } = JSON.parse(savedState);
        return {
          isOpen: savedIsOpen || false,
          isMinimized: savedIsMinimized || false
        };
      } catch (err) {
        console.warn('Failed to parse saved chat popup state:', err);
      }
    }
    return { isOpen: false, isMinimized: false };
  };

  const initialState = getInitialState();
  const [isOpen, setIsOpen] = useState(initialState.isOpen);
  const [isMinimized, setIsMinimized] = useState(initialState.isMinimized);
  const [unreadByRoom, setUnreadByRoom] = useState<Record<string, number>>({});
  // A prévia da lista de conversas sai da RPC `get_chat_rooms_summary`, que é
  // resolvida uma vez por organização e fica em cache — sem registrar aqui as
  // mensagens que chegam depois, a lista mostra para sempre a última mensagem
  // de quando o chat foi aberto pela primeira vez.
  const [lastMessageByRoom, setLastMessageByRoom] = useState<Record<string, UltimaMensagemDaSala>>({});
  const [hasNewMentions, setHasNewMentions] = useState(false);
  const [toasts, setToasts] = useState<ChatToast[]>([]);

  const unreadCount = useMemo(
    () => Object.values(unreadByRoom).reduce((sum, count) => sum + count, 0),
    [unreadByRoom]
  );

  // Salvar estado quando mudar
  React.useEffect(() => {
    const state = { isOpen, isMinimized };
    localStorage.setItem('chatPopupState', JSON.stringify(state));
  }, [isOpen, isMinimized]);

  // Listener para garantir que o estado seja preservado durante downloads
  React.useEffect(() => {
    const handleBeforeUnload = () => {
      const state = { isOpen, isMinimized };
      localStorage.setItem('chatPopupState', JSON.stringify(state));
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && isOpen) {
        const state = { isOpen, isMinimized };
        localStorage.setItem('chatPopupState', JSON.stringify(state));
        console.log('Chat popup state saved due to visibility change:', state);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isOpen, isMinimized]);

  const openChat = useCallback(() => {
    setIsOpen(true);
    // Não forçar isMinimized, mantendo o estado salvo
    // O usuário pode preferir abrir minimizado ou expandido

    // Só limpar o indicador de menções se não estiver minimizado
    // Contadores por sala são limpos individualmente ao abrir cada sala (selectRoom)
    if (!isMinimized) {
      setHasNewMentions(false);
    }
  }, [isMinimized]);

  const closeChat = useCallback(() => {
    setIsOpen(false);
    setIsMinimized(false);
  }, []);

  const minimizeChat = useCallback(() => {
    if (isOpen) {
      setIsMinimized(true);
    }
  }, [isOpen]);

  const maximizeChat = useCallback(() => {
    if (isOpen) {
      setIsMinimized(false);
      // Limpar indicador de menções ao maximizar
      setHasNewMentions(false);
    }
  }, [isOpen]);

  const toggleChat = useCallback(() => {
    if (isOpen) {
      if (isMinimized) {
        maximizeChat();
      } else {
        closeChat();
      }
    } else {
      openChat();
    }
  }, [isOpen, isMinimized, openChat, closeChat, maximizeChat]);

  const incrementUnreadCount = useCallback((roomId: string) => {
    setUnreadByRoom(prev => ({ ...prev, [roomId]: (prev[roomId] || 0) + 1 }));
  }, []);

  // Contagem vinda do banco (RPC get_chat_unread_counts). O estado local é a
  // soma dos eventos Realtime desta sessão e pode estar à FRENTE do banco, que
  // ainda não viu a mensagem que acabou de chegar — por isso fica o maior dos
  // dois, nunca a substituição cega.
  const hidratarNaoLidas = useCallback((porSala: Record<string, number>, ignorar: string[] = []) => {
    const ignoradas = new Set(ignorar);
    setUnreadByRoom(prev => {
      const next = { ...prev };
      let mudou = false;

      for (const [roomId, total] of Object.entries(porSala)) {
        if (ignoradas.has(roomId) || !(total > 0)) continue;
        if ((next[roomId] || 0) >= total) continue;
        next[roomId] = total;
        mudou = true;
      }

      return mudou ? next : prev;
    });
  }, []);

  const registerRoomMessage = useCallback((roomId: string, conteudo: string, em: string) => {
    setLastMessageByRoom(prev => {
      const atual = prev[roomId];
      // Duas mensagens quase simultâneas chegam fora de ordem: fica a mais nova.
      if (atual && atual.em >= em) return prev;
      return { ...prev, [roomId]: { conteudo, em } };
    });
  }, []);

  const clearRoomUnread = useCallback((roomId: string) => {
    setUnreadByRoom(prev => {
      if (!prev[roomId]) return prev;
      const next = { ...prev };
      delete next[roomId];
      return next;
    });
  }, []);

  const clearNotifications = useCallback(() => {
    setUnreadByRoom({});
    setHasNewMentions(false);
  }, []);

  const pushToast = useCallback((toast: Omit<ChatToast, 'id' | 'timestamp'>) => {
    setToasts(prev => [
      ...prev.slice(-3),
      { ...toast, id: `${toast.roomId}-${crypto.randomUUID()}`, timestamp: new Date() }
    ]);
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const contextValue: ChatPopupContextType = useMemo(() => ({
    isOpen,
    isMinimized,
    unreadCount,
    unreadByRoom,
    lastMessageByRoom,
    hasNewMentions,
    toasts,
    openChat,
    closeChat,
    minimizeChat,
    maximizeChat,
    toggleChat,
    incrementUnreadCount,
    hidratarNaoLidas,
    registerRoomMessage,
    clearRoomUnread,
    setHasNewMentions,
    clearNotifications,
    pushToast,
    dismissToast
  }), [
    isOpen,
    isMinimized,
    unreadCount,
    unreadByRoom,
    lastMessageByRoom,
    hasNewMentions,
    toasts,
    openChat,
    closeChat,
    minimizeChat,
    maximizeChat,
    toggleChat,
    incrementUnreadCount,
    hidratarNaoLidas,
    registerRoomMessage,
    clearRoomUnread,
    setHasNewMentions,
    clearNotifications,
    pushToast,
    dismissToast
  ]);

  return (
    <ChatPopupContext.Provider value={contextValue}>
      {children}
    </ChatPopupContext.Provider>
  );
}